import { IdentityCheckResult, BreachRecord } from '../types';
import { KNOWN_BREACHES } from '../data/breaches';

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export async function checkIdentityExposure(
  query: string, 
  type: 'email' | 'phone' | 'name' | 'username'
): Promise<IdentityCheckResult> {
  await delay(700); // realistic search simulation

  const cleanQuery = query.trim().toLowerCase();
  
  if (!cleanQuery) {
    throw new Error('Please enter a valid search term.');
  }

  // Realistic mock matching logic
  // If query contains 'safe' or 'clean' or 'testclean', return 0 breaches
  // If query matches known email or contains '@', return matching or representative breaches
  const isClean = cleanQuery.includes('safe') || cleanQuery.includes('clean') || cleanQuery.includes('nodata');
  
  let matchingBreaches: BreachRecord[] = [];
  
  if (!isClean) {
    if (cleanQuery.includes('medibank') || cleanQuery.includes('health')) {
      matchingBreaches = KNOWN_BREACHES.filter(b => b.id.includes('medibank'));
    } else if (cleanQuery.includes('optus') || cleanQuery.includes('telco')) {
      matchingBreaches = KNOWN_BREACHES.filter(b => b.id.includes('optus'));
    } else if (cleanQuery.includes('mitchell') || cleanQuery.includes('alex') || cleanQuery.includes('gmail') || cleanQuery.includes('yahoo') || cleanQuery.includes('hotmail')) {
      // Common simulated breach match
      matchingBreaches = [KNOWN_BREACHES[3], KNOWN_BREACHES[4]]; // Ticketmaster & Canva
    } else if (cleanQuery.includes('04') || type === 'phone') {
      matchingBreaches = [KNOWN_BREACHES[1], KNOWN_BREACHES[5]]; // Optus & Parcel
    } else {
      // Default to 1-2 breaches for general test queries, or randomly pick based on length
      const count = (cleanQuery.length % 3) + 1;
      matchingBreaches = KNOWN_BREACHES.slice(0, count);
    }
  }

  const exposedDataClasses = Array.from(
    new Set(matchingBreaches.flatMap(b => b.dataClasses))
  );

  const exposureStatus = matchingBreaches.length === 0 
    ? 'none' 
    : matchingBreaches.length > 2 
      ? 'confirmed' 
      : 'potential';

  const recommendedActions = matchingBreaches.length === 0 
    ? [
        'Enable continuous monitoring to detect future breaches immediately.',
        'Ensure unique 16+ character passphrases across all accounts.',
        'Enable Multi-Factor Authentication (MFA) on your primary email.'
      ]
    : [
        'Change passwords on all affected and linked services immediately.',
        'Never reuse passwords across Australian banking, email, and myGov.',
        'Enable app-based Multi-Factor Authentication (MFA) or Passkeys.',
        'Watch for targeted phishing emails and SMS delivery scams referencing this data.',
        'Consider a free 21-day credit ban with Equifax, Experian, and illion.'
      ];

  return {
    query: cleanQuery,
    type,
    checkedAt: new Date().toISOString(),
    exposureStatus,
    breachCount: matchingBreaches.length,
    breaches: matchingBreaches,
    exposedDataClasses,
    recommendedActions
  };
}
